import React from "react";
import { useTranslation } from "react-i18next";
import OSProductivityTable, { ProdRow } from "./OSProductivityTable";
import OSRazmerTable, { RazmerRow } from "./OSRazmerTable";
import { RNRazmerLotMap } from "../hooks/useRNRazmerLots";
import { RazmerAutosaveField, RazmerCellSaveStatusMap } from "../hooks/useRNRazmerAutosave";
import OSMaterialnoTable, { MatGroup } from "./OSMaterialnoTable";
import type { RazmerCopySource } from "../utils/rnMaterialnoCopySource";
import RNMaterialPriemTable, { RNMaterialPriemRow } from "./RNMaterialPriemTable";
import { SubprocessItemRow } from "./OSSubprocessItemsTable";
import OSSubprocessEntriesTable from "./OSSubprocessEntriesTable";
import { SubprocessOption } from "../select/SubprocessCells";
import type { RNInterProductReceipt } from "../types/Types";

/**
 * The right-hand column of the OS editor: everything that belongs to the stage currently
 * selected in the left stage tree.
 *
 * Two shapes, decided by `isRazmerStage`:
 *  - Размерување is a PINNED, process-level stage with no Subprocess document behind it. It
 *    shows the weighing table with ALL of the normativ's rows, and none of the per-subprocess
 *    cards (a process-level stage has no machines, no InProcess, no montage params).
 *  - every other stage is a real Subprocess: Продуктивност, Материјално, and the two
 *    codebook-driven entry tables, each scoped to that stage.
 *
 * Presentational only. The rows handed in are already the active stage's slice, and the
 * onChange callbacks hand plain arrays back — stamping rows with the active subprocess and
 * marking the form dirty stays with the owner of that state, not here.
 *
 * There is NO Save button here. The panel used to carry one, but it called the very same
 * handleSaveIngredient as the master header's — one PUT of the whole normativ, not a
 * per-stage save — while skipping the header's `isDirty` gate, so it could fire a pointless
 * save (and a version bump) with nothing changed. Saving is global and lives in the master
 * header only.
 */

interface Props {
  /** "os" (default) or "rn" — forwarded to the child tables that vary their columns by caller. */
  mode?: "os" | "rn";
  /** Rendered at the very top of the panel, before Продуктивност — e.g. RN's per-stage Save button. */
  headerSlot?: React.ReactNode;

  isRazmerStage: boolean;

  /** RN-PROST (simple layout) only: hides the Log Book (Продуктивност) table. */
  hideProductivity?: boolean;
  productivityRows: ProdRow[];
  onProductivityChange: (rows: ProdRow[]) => void;
  productivityMachineIds?: string[];
  groupProductivityBySubprocess?: boolean;
  subprocessNameById?: Map<string, string>;
  editableProductivitySummary?: boolean;
  onMontageStartCommit?: () => void;
  onMontageStartClear?: () => void;
  onMontageEndCommit?: () => void;
  onMontageEndClear?: () => void;
  onTimingCommit?: (field: any) => void;

  razmerRows: RazmerRow[];
  onRazmerChange: (rows: RazmerRow[]) => void;
  onRazmerChangeAndSave?: (rows: RazmerRow[]) => void;
  onRazmerPreviewChange?: (rows: RazmerRow[]) => void;
  razmerToolbarSlot?: React.ReactNode;
  hideRazmerCopyButton?: boolean;
  razmerIsSaving?: boolean;
  /** RN-PROST (simple layout) only: enables the Размерување Column Manager panel. */
  razmerColumnManager?: boolean;
  /** RN only: read-only lot suggestion by osRowId. */
  razmerLotSuggestions?: RNRazmerLotMap;
  razmerHiddenColumns?: string[];
  onRazmerColumnToggle?: (columnKey: string) => void;
  onRazmerColumnDrop?: (columnKey: string) => void;
  /** RN only: autosave feedback for Кол. раз./Отпад/Растур + the blur that triggers it. */
  razmerCellSaveStatus?: RazmerCellSaveStatusMap;
  onRazmerActualCommit?: (rowIndex: number, field: RazmerAutosaveField) => void;

  materialnoGroups: MatGroup[];
  onMaterialnoChange: (groups: MatGroup[]) => void;
  onMaterialnoChangeAndSave?: (groups: MatGroup[]) => void;
  materialnoIsSaving?: boolean;
  /** RN Материјално: Copy unlocked only after matching Размерување dispatch. */
  materialnoAllowCopyFromRazmer?: boolean;
  /** RN Материјално: itemId → Количина размерена + Серија + Рок from Размерување. */
  materialnoRazmerCopySourceByItemId?: Record<string, RazmerCopySource>;
  /** RN Материјално: extra toolbar actions (e.g. Издај материјал). */
  materialnoToolbarSlot?: React.ReactNode;
  materialnoLockUnsavedRows?: boolean;

  /**
   * RN-only "Материјално прием" (7201) rows for the active stage — interProducts[]
   * rows PRODUCED by this stage. Rendered immediately after OSMaterialnoTable, RN
   * mode only; undefined/empty on /os (isRazmerStage also hides it, same as
   * materialno — a process-level weighing stage has no interProducts of its own).
   */
  interProductRows?: RNMaterialPriemRow[];
  onInterProductChange?: (rows: SubprocessItemRow[]) => void;
  onInterProductChangeAndSave?: (rows: SubprocessItemRow[]) => void;
  interProductIsSaving?: boolean;
  /** RN Материјално прием: the "Прими" (receive-7201) button. */
  interProductToolbarSlot?: React.ReactNode;
  rnWorkOrderId?: string;
  rnWorkOrderNumber?: string;
  interProductReceipts?: RNInterProductReceipt[];
  onSamplingApproval?: () => void;

  /** The Subprocess behind the active stage — undefined on the pinned weighing stage. */
  activeSubprocess?: SubprocessOption;
  /** RN-only fixed machine repair table, rendered beside subprocess entry cards. */
  machineRepairsSlot?: React.ReactNode;
  inProcessValues: Record<string, string>;
  onInProcessChange: (entryId: string, value: string) => void;
  montageValues: Record<string, string>;
  onMontageChange: (entryId: string, value: string) => void;
}

const OSStagePanel: React.FC<Props> = ({
  mode = "os",
  headerSlot,
  isRazmerStage,
  hideProductivity = false,
  productivityRows,
  onProductivityChange,
  productivityMachineIds,
  groupProductivityBySubprocess = false,
  subprocessNameById,
  editableProductivitySummary = false,
  onMontageStartCommit,
  onMontageStartClear,
  onMontageEndCommit,
  onMontageEndClear,
  onTimingCommit,
  razmerRows,
  onRazmerChange,
  onRazmerChangeAndSave,
  onRazmerPreviewChange,
  razmerToolbarSlot,
  hideRazmerCopyButton,
  razmerIsSaving,
  razmerColumnManager = false,
  razmerLotSuggestions,
  razmerHiddenColumns,
  onRazmerColumnToggle,
  onRazmerColumnDrop,
  razmerCellSaveStatus,
  onRazmerActualCommit,
  materialnoGroups,
  onMaterialnoChange,
  onMaterialnoChangeAndSave,
  materialnoIsSaving,
  materialnoAllowCopyFromRazmer = false,
  materialnoRazmerCopySourceByItemId,
  materialnoToolbarSlot,
  materialnoLockUnsavedRows = false,
  interProductRows,
  onInterProductChange,
  onInterProductChangeAndSave,
  interProductIsSaving,
  interProductToolbarSlot,
  rnWorkOrderId,
  rnWorkOrderNumber,
  interProductReceipts,
  onSamplingApproval,
  activeSubprocess,
  machineRepairsSlot,
  inProcessValues,
  onInProcessChange,
  montageValues,
  onMontageChange,
}) => {
  const { t } = useTranslation();
  return (
  <div className="flex-1 min-w-0">
    {headerSlot}

    {/* Always visible on RN — machines/repairs for the current (or fallback) subprocess. */}
    {machineRepairsSlot}

    {/* Продуктивност — per-machine timing table. Rendered before the stage-specific table. */}
    {!hideProductivity && (
      <OSProductivityTable
        mode={mode}
        rows={productivityRows}
        onChange={onProductivityChange}
        allowedMachineIds={productivityMachineIds}
        readOnlyMachineSelect
        groupBySubprocess={groupProductivityBySubprocess}
        subprocessNameById={subprocessNameById}
        editableSummary={editableProductivitySummary}
        onMontageStartCommit={onMontageStartCommit}
        onMontageStartClear={onMontageStartClear}
        onMontageEndCommit={onMontageEndCommit}
        onMontageEndClear={onMontageEndClear}
        onTimingCommit={onTimingCommit}
      />
    )}

    {/* Размерување → the pinned weighing table (all rows); any other stage → Материјално. */}
    {isRazmerStage ? (
      <OSRazmerTable
        mode={mode}
        rows={razmerRows}
        onChange={onRazmerChange}
        onChangeAndSave={onRazmerChangeAndSave}
        onPreviewChange={onRazmerPreviewChange}
        leadingActions={razmerToolbarSlot}
        hideCopyButton={hideRazmerCopyButton}
        isSaving={razmerIsSaving}
        lotSuggestions={razmerLotSuggestions}
        enableColumnManager={razmerColumnManager}
        hiddenColumns={razmerHiddenColumns}
        onColumnToggle={onRazmerColumnToggle}
        onColumnDrop={onRazmerColumnDrop}
        cellSaveStatus={razmerCellSaveStatus}
        onActualCommit={onRazmerActualCommit}
      />
    ) : (
      <>
        <OSMaterialnoTable
          mode={mode}
          groups={materialnoGroups}
          onChange={onMaterialnoChange}
          onChangeAndSave={onMaterialnoChangeAndSave}
          isSaving={materialnoIsSaving}
          allowCopyFromRazmer={materialnoAllowCopyFromRazmer}
          razmerCopySourceByItemId={materialnoRazmerCopySourceByItemId}
          leadingActions={materialnoToolbarSlot}
          lockUnsavedRows={materialnoLockUnsavedRows}
        />
        {/* RN-only "Материјално прием" (7201) — /os never passes interProductRows, so
            this stays unrendered there regardless of the mode guard below. */}
        {mode === 'rn' && onInterProductChange && (
          <RNMaterialPriemTable
            rows={interProductRows || []}
            onChange={onInterProductChange}
            onChangeAndSave={onInterProductChangeAndSave}
            isSaving={interProductIsSaving}
            leadingActions={interProductToolbarSlot}
            rnWorkOrderId={rnWorkOrderId}
            rnWorkOrderNumber={rnWorkOrderNumber}
            interProductReceipts={interProductReceipts}
            onSamplingApproval={onSamplingApproval}
          />
        )}
      </>
    )}

    {/* Codebook-driven entry tables — subprocess-level, so hidden on the weighing stage. */}
    {!isRazmerStage && (
      <OSSubprocessEntriesTable
        title={t('os.panel.inProcess')}
        entriesLabel={t('os.panel.inProcess')}
        entries={activeSubprocess?.inProcess}
        subprocessName={activeSubprocess?.name}
        values={inProcessValues}
        onChange={onInProcessChange}
      />
    )}

    {!isRazmerStage && (
      <OSSubprocessEntriesTable
        title={t('os.panel.montageParams')}
        entriesLabel={t('os.panel.montageParams')}
        entries={activeSubprocess?.montageParams}
        subprocessName={activeSubprocess?.name}
        values={montageValues}
        onChange={onMontageChange}
      />
    )}
  </div>
  );
};

export default OSStagePanel;
